/**
 * NotificationsScreen — Full-screen modal content for notifications
 *
 * Two views:
 * 1. List view — notification cells (icon + text)
 * 2. Detail view — friend request Accept/Decline (slides in from right)
 *
 * Passed as content to UIManager.openModal('notifications', element).
 */

import type { GameNotification } from '@sdl/shared';
import type { NetworkManager } from '../../network/NetworkManager';
import { NotificationCell } from '../components/notifications/NotificationCell';
import { ImageViewer } from '../components/ImageViewer';

export class NotificationsScreen {
  public element: HTMLElement;
  private listEl: HTMLElement;
  private emptyEl: HTMLElement;
  private detailEl: HTMLElement;
  private cells: Map<string, NotificationCell> = new Map();
  private networkManager: NetworkManager | null = null;
  private showingDetail = false;

  constructor() {
    this.element = document.createElement('div');
    this.element.className = 'notif-screen';

    // List view
    this.listEl = document.createElement('div');
    this.listEl.className = 'notif-list';

    this.emptyEl = document.createElement('div');
    this.emptyEl.className = 'crew-empty-state';
    this.emptyEl.innerHTML = `
      <div class="crew-empty-icon">🔔</div>
      <div class="crew-empty-title">ALL CLEAR</div>
      <div class="crew-empty-subtitle">No notifications yet</div>
    `;

    // Detail view (friend request)
    this.detailEl = document.createElement('div');
    this.detailEl.className = 'notif-detail';

    this.element.appendChild(this.listEl);
    this.element.appendChild(this.emptyEl);
    this.element.appendChild(this.detailEl);

    this.injectStyles();
    this.updateEmptyState();
  }

  initialize(networkManager: NetworkManager): void {
    this.networkManager = networkManager;
  }

  setNotifications(notifications: GameNotification[]): void {
    this.cells.forEach(cell => cell.destroy());
    this.cells.clear();
    this.listEl.innerHTML = '';

    const sorted = [...notifications].sort((a, b) => b.createdAt - a.createdAt);
    for (const notif of sorted) {
      this.addCellInternal(notif, false);
    }
    this.updateEmptyState();
  }

  addNotification(notif: GameNotification): void {
    this.addCellInternal(notif, true);
    this.updateEmptyState();
  }

  removeNotification(notificationId: string): void {
    const cell = this.cells.get(notificationId);
    if (cell) {
      cell.animateRemoval().then(() => {
        this.cells.delete(notificationId);
        this.updateEmptyState();
      });
    }
    // If detail view is showing this notification, go back to list
    if (this.showingDetail) {
      this.hideDetail();
    }
  }

  onOpen(): void {
    this.networkManager?.sendNotificationsSeen();
    this.cells.forEach(cell => {
      cell.element.classList.remove('notif-unseen');
    });
    // Always start on list view
    if (this.showingDetail) {
      this.hideDetail();
    }
  }

  private addCellInternal(notif: GameNotification, prepend: boolean): void {
    if (this.cells.has(notif.id)) return;

    const cell = new NotificationCell(notif);

    cell.on('tap', ({ notification }) => {
      if (notification.type === 'friend_request') {
        this.showFriendRequestDetail(notification);
      }
    });

    this.cells.set(notif.id, cell);

    if (prepend && this.listEl.firstChild) {
      this.listEl.insertBefore(cell.element, this.listEl.firstChild);
    } else {
      this.listEl.appendChild(cell.element);
    }
  }

  private showFriendRequestDetail(notif: GameNotification): void {
    const { fromUsername, fromPicture, id } = notif;
    const initial = fromUsername.charAt(0).toUpperCase();
    const avatarContent = fromPicture
      ? `<img src="${fromPicture}" alt="${initial}" class="notif-detail-avatar-img">`
      : `<span class="notif-detail-avatar-initial">${initial}</span>`;

    this.detailEl.innerHTML = `
      <button class="notif-detail-back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        <span>Back</span>
      </button>
      <div class="notif-detail-content">
        <div class="notif-detail-avatar">${avatarContent}</div>
        <div class="notif-detail-name">${fromUsername}</div>
        <div class="notif-detail-label">wants to be your friend</div>
        <div class="notif-detail-actions">
          <button class="notif-detail-accept">ACCEPT</button>
          <button class="notif-detail-decline">DECLINE</button>
        </div>
      </div>
    `;

    // Wire handlers
    this.detailEl.querySelector('.notif-detail-back')!.addEventListener('click', () => {
      this.hideDetail();
    });

    // Tap the avatar photo → full-screen pinch-to-zoom viewer.
    if (fromPicture) {
      this.detailEl.querySelector('.notif-detail-avatar-img')?.addEventListener('click', () => {
        ImageViewer.open(fromPicture);
      });
    }

    this.detailEl.querySelector('.notif-detail-accept')!.addEventListener('click', () => {
      this.networkManager?.sendFriendRequestAccept(id);
      this.removeNotification(id);
    });

    this.detailEl.querySelector('.notif-detail-decline')!.addEventListener('click', () => {
      this.networkManager?.sendFriendRequestDecline(id);
      this.removeNotification(id);
    });

    // Show detail, hide list
    this.showingDetail = true;
    this.listEl.style.display = 'none';
    this.emptyEl.style.display = 'none';
    this.detailEl.classList.add('active');
  }

  private hideDetail(): void {
    this.showingDetail = false;
    this.detailEl.classList.remove('active');
    this.detailEl.innerHTML = '';
    this.updateEmptyState();
  }

  private updateEmptyState(): void {
    if (this.showingDetail) return;
    const hasItems = this.cells.size > 0;
    this.listEl.style.display = hasItems ? 'flex' : 'none';
    this.emptyEl.style.display = hasItems ? 'none' : 'flex';
  }

  private injectStyles(): void {
    if (document.getElementById('notif-screen-styles')) return;
    const style = document.createElement('style');
    style.id = 'notif-screen-styles';
    style.textContent = `
.notif-screen {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.notif-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;
}

/* ─── Notification Cell ─── */
.notif-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  transition: transform 0.3s ease, opacity 0.3s ease;
  cursor: pointer;
}

.notif-cell:active {
  background: rgba(255, 255, 255, 0.06);
}

.notif-unseen {
  background: rgba(255, 215, 0, 0.04);
}

/* Avatar */
.notif-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.notif-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.notif-avatar-initial {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.6);
}

/* Body */
.notif-body {
  flex: 1;
  min-width: 0;
}

.notif-message {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.3;
}

.notif-message strong {
  color: #fff;
}

.notif-stacks {
  color: #00FF88;
  font-weight: 600;
}

.notif-time {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 2px;
}

/* Chevron */
.notif-chevron {
  flex-shrink: 0;
  opacity: 0.4;
}

/* ─── Detail View ─── */
.notif-detail {
  display: none;
  flex-direction: column;
  height: 100%;
}

.notif-detail.active {
  display: flex;
}

.notif-detail-back {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-start;
}

.notif-detail-back:active {
  opacity: 0.6;
}

.notif-detail-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  gap: 12px;
}

.notif-detail-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid rgba(255, 0, 128, 0.3);
}

.notif-detail-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: zoom-in;
}

.notif-detail-avatar-initial {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 32px;
  color: rgba(255, 255, 255, 0.6);
}

.notif-detail-name {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 24px;
  color: #fff;
  letter-spacing: 1px;
}

.notif-detail-label {
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 8px;
}

.notif-detail-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.notif-detail-accept,
.notif-detail-decline {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 16px;
  letter-spacing: 1.5px;
  padding: 10px 32px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s;
}

.notif-detail-accept:active,
.notif-detail-decline:active {
  opacity: 0.7;
}

.notif-detail-accept {
  background: rgba(0, 255, 136, 0.15);
  color: #00FF88;
  border: 1px solid rgba(0, 255, 136, 0.25);
}

.notif-detail-decline {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
    `;
    document.head.appendChild(style);
  }
}
